<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

// Two-step tracker at the top of the sell drawer (Figma 34842:194851 /
// 34842:194912): numbered node over its label, a rule between the nodes.
// Reached steps (done + current) stay filled, and the rule turns brand once
// the second step is reached.

interface Props {
  labels: readonly string[]
  /** 1-based active step. */
  current: number
}

const props = defineProps<Props>()

const isReached = (index: number) => index + 1 <= props.current
</script>

<template>
  <Box
    is="ol"
    align="start"
    class="sell-stepper"
    :aria-label="`Step ${current} of ${labels.length}`"
  >
    <template v-for="(label, index) in labels" :key="label">
      <Box
        v-if="index > 0"
        is="li"
        role="presentation"
        :class="['sell-stepper__bar', { 'sell-stepper__bar--reached': isReached(index) }]"
        aria-hidden="true"
      />
      <Box
        is="li"
        direction="col"
        align="center"
        gap="10px"
        class="sell-stepper__step"
        :aria-current="index + 1 === current ? 'step' : undefined"
      >
        <Box
          align="center"
          justify="center"
          :class="['sell-stepper__node', { 'sell-stepper__node--reached': isReached(index) }]"
        >
          <Text
            is="span"
            type="body"
            weight="semibold"
            :color="isReached(index) ? 'inverse' : 'var(--p-surface-500)'"
            class="sell-stepper__number"
            >{{ index + 1 }}</Text
          >
        </Box>
        <Text
          is="span"
          type="heading"
          :weight="isReached(index) ? 'semibold' : 'regular'"
          :color="isReached(index) ? 'default' : 'var(--p-surface-500)'"
          class="sell-stepper__label"
          >{{ label }}</Text
        >
      </Box>
    </template>
  </Box>
</template>

<style scoped>
.sell-stepper {
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sell-stepper__step {
  flex-shrink: 0;
}

.sell-stepper__node {
  width: 26px;
  height: 26px;
  border-radius: 40px;
  background: var(--p-surface-100);
}

.sell-stepper__node--reached {
  background: var(--p-primary-600);
}

.sell-stepper__number {
  line-height: 1.2;
}

.sell-stepper__label {
  line-height: 1.3;
  white-space: nowrap;
}

/* Rule sits on the nodes' centre line, between the two step columns. */
.sell-stepper__bar {
  flex: 1;
  height: 2px;
  margin: 12px 1rem 0;
  background: var(--p-sliceal-gray-200);
}

.sell-stepper__bar--reached {
  background: var(--p-primary-500);
}

@media (max-width: 992px) {
  .sell-stepper__bar {
    margin: 12px 0.5rem 0;
  }
}
</style>
